<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-process-list-drawer">
    <el-drawer
      :title="title"
      :modal="false"
      :visible.sync="visible"
      size="426px"
      @closed="handleClosed"
      direction="rtl">
      <el-tabs v-model="activeName" @tab-click="handleClick" :class="{'notification-only':!isAlertListAllowed}">
        <el-tab-pane v-if="isAlertListAllowed" :label="alertTitle" name="alerts">
          <wy-autoQA-trending-topic-alert-template v-if="isC1Interactions||isC1AutoQA||isC1SmartTags" :controller="module"></wy-autoQA-trending-topic-alert-template>
          <wy-bot-analytics-alert-template v-else :controller="module"></wy-bot-analytics-alert-template>
        </el-tab-pane>
        <el-tab-pane v-if="isProcessListAllowed" :label="processTitle" name="activities">
          <div v-if="visible" class="wy-process-panel"  @click="handleDrawerClick">
            <div class="wy-process-panel-item" :class="{'active': processItem.isNew}" v-for="(processItem, index) in processList" :key="index" @mouseover="processPanelMouseOver">
              <div class="div-item div-header-title">
                <span v-if="processItem.isNew" class="blue-dot"></span>
                <span class="actionBy">{{getActionBy(processItem)}}</span>
                <span class="dot">&nbsp;</span>
                <span class="date">{{getDate(processItem.actionDate)}}</span>
              </div>
              <div v-if="processItem.processStatus=='finished'" class="status-icon completed-green"></div>
              <div v-if="processItem.processStatus=='failed'" class="status-icon failed-red"></div>
              <div v-if="processItem.processStatus=='pending'||processItem.processStatus=='running'" class="status-icon pending-yellow"></div>
              <div class="div-item div-title">
                {{getProcessName(processItem)}} {{firstCap(processItem.processStatus=="finished" ? "Completed" : processItem.processStatus)}}
                <div v-if="processItem.action == 'rename'" class="div-item addition-info">
                  <p class="ellipsis" @mouseover="mouseover($event, processItem.detail.topicDisplayName + ' to ' + processItem.detail.topicDisplayNameNew)" @mouseout="mouseout($event)">
                    for topic "{{processItem.detail.topicDisplayName}}" to "{{processItem.detail.topicDisplayNameNew}}"
                  </p>
                </div>
                <div v-else-if="processItem.action == 'merge' && processItem.detail.topicDisplayNamesToMerge" class="div-item addition-info ellipsis">
                  <p class="ellipsis" @mouseover="mouseover($event, processItem.detail.topicDisplayNamesToMerge.join(', ') + ' to ' + processItem.detail.topicDisplayName)" @mouseout="mouseout">
                    for {{processItem.detail.topicDisplayNamesToMerge.length>1 ? "topics": "topic"}} "{{processItem.detail.topicDisplayNamesToMerge.join('", "')}}" to "{{processItem.detail.topicDisplayName}}"
                  </p>
                </div>
                <div v-else-if="processItem.action == 'unmerge' && processItem.detail.topicNames" class="div-item addition-info">
                  <p class="ellipsis" @mouseover="mouseover($event, processItem.detail.topicNames.join(', ') + ' from ' + processItem.detail.topicDisplayName)" @mouseout="mouseout">
                    for {{processItem.detail.topicNames.length>1 ? "topics": "topic"}} "{{processItem.detail.topicNames.join('", "')}}" from "{{processItem.detail.topicDisplayName}}"
                  </p>
                </div>
                <div v-else-if="processItem.action == 'blacklist' && processItem.detail.topicDisplayNames" class="div-item addition-info">
                  <p class="ellipsis"></p>
                  <p class="ellipsis" @mouseover="mouseover($event, processItem.detail.topicDisplayNames.join(', '))" @mouseout="mouseout">
                    for {{processItem.detail.topicDisplayNames.length>1 ? "topics": "topic"}} "{{processItem.detail.topicDisplayNames.join('", "')}}"
                  </p>
                </div>
                <div v-else-if="processItem.action == 'unblacklist'" class="div-item addition-info">
                  <p class="ellipsis" @mouseover="mouseover($event, processItem.detail.topicDisplayName)" @mouseout="mouseout">
                    for topic "{{processItem.detail.topicDisplayName}}"
                  </p>
                </div>
                <div v-else-if="processItem.action=='etl'||processItem.action=='etlReportIntent'||processItem.action=='reload'||processItem.action=='reloadReportIntent'" class="div-item addition-info">
                  <div v-if="processItem.detail.fromDate || processItem.detail.toDate">
                    <p class="ellipsis">
                      for data from {{getDate(processItem.detail.fromDate) || 'oldest'}} to {{getDate(processItem.detail.toDate) || 'latest'}}
                    </p>
                  </div>
                  <div v-else>
                    <p class="ellipsis">
                      for all data
                    </p>
                  </div>
                </div>
              </div>
              <div class="div-item time-range">
                <div class="label">Time</div>
                <div class="content">{{getTime(processItem.startedDate) || 'oldest'}}{{processItem.finishedDate ? " - " + getTime(processItem.finishedDate) : "" }}</div>
              </div>
              <div class="div-item process-id">
                <div class="label">ID</div>
                <div class="content">{{processItem.processId}}</div>
              </div>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
      
    </el-drawer>

    <el-popover
      ref="tooltip"
      placement="left"
      width="360"
      popper-class="wy-popover-tooltip-processlist"
      trigger="manual">
      <div class="content processId"><label>ProcessID:</label> {{tooltipProcessId}}</div>      
    </el-popover>

    <el-tooltip
      ref="alertTooltip"
      placement="bottom"
      effect="dark">
      <div slot="content"><span v-html="alertTooltipContent"></span></div>
    </el-tooltip>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions} from 'vuex';
import WyUtils from '../library/wy-utils.js';
import moment from 'moment';
import WyAutoQATrendingTopicAlertTemplate from './templates/wy-autoQA-trending-topic-alert-template.vue';
import WyBotAnalyticsAlertTemplate from './templates/wy-bot-analytics-alert-template.vue';

export default Vue.extend({
  name: 'wy-process-list-drawer',
  components: {
    'wy-bot-analytics-alert-template': WyBotAnalyticsAlertTemplate,
    'wy-autoQA-trending-topic-alert-template': WyAutoQATrendingTopicAlertTemplate
  },
  props: ['controller'],
  data() {
    return {
      module: this,
      activeName: 'alerts',
      visible: false,
      tooltipProcessId: null,
      tooltipProcessDetail: null,
      alertTooltipContent: null,

      activitiesRead: false,
      alertsRead: false,

      reverseMetrics: ['negativeFeedbackScore'],

      title: '',

      notificationViewTime: null,

      // Used for amplitude event properties mapping, fetched from AlertPipelines.json
      amplitudeMetricMapper: {
        bes : 'overall_Bot_Experience_Score_alert_count',
        bas : 'overall_Bot_Automation_Score_alert_count',
        vaVolume : 'overall_Total_VA_Conversations_alert_count',
        vaContainedVolume : 'overall_VA_Conversation_Contained_alert_count',
        totalMessages : 'overall_Customer_Messages_alert_count',
        containedConversationsTrueResolvedVolume : 'overall_True_Resolved_alert_count',
        understandingPercent : 'overall_NLU_alert_count',
        negativeFeedbackScore : 'overall_Negative_Feedback_Score_alert_count',

        avgBes : 'topic_Bot_Experience_Score_alert_count',
        avgBas : 'topic_Bot_Automation_Score_alert_count',
        avgAes : 'topic_Agent_Experience_Score_alert_count',
        volume : 'topic_Volume_alert_count',
        containment : 'topic_Containment_alert_count',
      }
    }
  },

  computed: {
    ...mapGetters(['isRoleAllow', 'branding', 'alertList', 'processList', 'alertNewCounter', 'processNewCounter', 'processAlertStatus', 'getBrowserLocalStorageByUser', 'isC1BotAnaltyics', 'isC1Interactions', 'isC1AutoQA', 'isC1SmartTags']),

    alertTitle() {
      if(this.alertNewCounter > 0) {
        return `Alerts (${this.alertNewCounter})`;
      }
      return "Alerts";
    },

    processTitle() {
      if(this.processNewCounter > 0) {
        return `Activities (${this.processNewCounter})`;
      }
      return "Activities";
    },

    hideTitle() {
      return this.branding != Vue.WyConstants.PRODUCT_NAME_OC;
    },

    isAlertListAllowed() {
      if(this.isC1Interactions) {
        return this.isRoleAllow(4604)
      }
      else if(this.isC1AutoQA) {
        return this.isRoleAllow(4704)
      }
      else if(this.isC1SmartTags) {
        return this.isRoleAllow(4903)
      }
      else {
        return this.isRoleAllow([3001, 900005]);
      }
    },

    isProcessListAllowed() {
      if(this.isC1Interactions) {
        return this.isRoleAllow(4601)
      }
      else if(this.isC1AutoQA) {
        return false
      }
      else if(this.isC1SmartTags) {
        return false
      }
      else {
        return this.isRoleAllow([4201, 900009, 900013])
      }
    }
  },

  methods: {
    ...mapActions(['fetchProcessList', 'resetProcessNewItems', 'resetAlertNewItems', 'fetchCompanyAlertSettings']),

    handleClick() {
      if(this.activeName == 'activities') {
        this.activitiesRead = true;
      }
      else if(this.activeName == 'alerts') {
        this.alertsRead = true;
      }
    },

    handleGoToConfigration() {
      this.visible = false;
      if(this.isC1Interactions) {
        this.$root.$emit(`fire-app-modal-events`, '/interactions/configuration/wy-interaction-configuration.vue');
      }
      else if(this.isC1AutoQA) {
        this.$root.$emit(`fire-app-modal-events`, '/autoQA/configuration/wy-autoQA-configuration.vue');
      }
      else if(this.isC1SmartTags) {
        this.$root.$emit(`fire-app-modal-events`, '/smartTag/configuration/wy-smart-tag-configuration.vue');
      }
      else {
        this.$root.$emit(`fire-app-modal-events`, '/analytics/configuration/wy-analytics-configuration.vue', {tabName: 'overallKPIs'});
      }
    },

    processPanelMouseOver(e) {
      if(e.toElement) {
        if(e.toElement.className != 'ellipsis has-tooltip') {
          let tooltipsEl = document.querySelector(".site-tooltips");
          tooltipsEl.style.display = 'none';
        }
      }
      else if(e.target) {
        if(e.target.className != 'ellipsis has-tooltip') {
          let tooltipsEl = document.querySelector(".site-tooltips");
          tooltipsEl.style.display = 'none';
        }
      }
      
    },
    alertsPanelMouseOver(e) {
      if(e.toElement) {
        if(e.toElement.className != 'label ellipsis has-tooltip') {
          let tooltipsEl = document.querySelector(".site-tooltips");
          tooltipsEl.style.display = 'none';
        }
      }
      else if(e.target) {
        if(e.target.className != 'label ellipsis has-tooltip') {
          let tooltipsEl = document.querySelector(".site-tooltips");
          tooltipsEl.style.display = 'none';
        }
      }
      
    },

    mouseover(e, content) {
      e.target.classList.add("has-tooltip")
      let tooltipsEl = document.querySelector(".site-tooltips");
      if(tooltipsEl) {
        tooltipsEl.classList.add('black-background');
        tooltipsEl.classList.add('notification');
        tooltipsEl.innerHTML = content
        tooltipsEl.style.display = 'block';
        tooltipsEl.style.left = `${e.target.getBoundingClientRect().x + 20}px`;
        tooltipsEl.style.right = "10px";
        if((window.innerHeight - e.target.getBoundingClientRect().top) < e.target.getBoundingClientRect().height + tooltipsEl.getBoundingClientRect().height) {
          tooltipsEl.style.top = `${document.body.offsetHeight -  tooltipsEl.getBoundingClientRect().height}px`;
        }
        else {
          tooltipsEl.style.top = `${e.target.getBoundingClientRect().top + e.target.getBoundingClientRect().height + 0}px`;
        }
      }
    },
    mouseout(e) {
      e.stopPropagation();
      let tooltipsEl = document.querySelector(".site-tooltips");
      if(tooltipsEl && e.toElement && e.toElement.className != 'site-tooltips black-background notification') {
        tooltipsEl.style.display = 'none';
      }
    },

    getActionBy(item) {
      const actionBy = item.actionByUsername ? item.actionByUsername : item.actionBy;
      return actionBy.replace("etl.process@wysdom.ai", "Wysdom’s ETL Process").replace("reload.process@wysdom.ai", "Wysdom’s Reload Process")
    },

    getPageName(item) {
      const metricName = Vue.WyConstants.ALERT_METRICS[item.metric];
      if(metricName && metricName.section) {
        if(this.isC1Interactions) {
          return 'Trending Topics'
        }
        else if(this.isC1SmartTags) {
          return 'Smart Tags'
        }
        else {
          return metricName.section
        }
      }
      return "";
    },

    getMetricName(item) {
      const metricName = Vue.WyConstants.ALERT_METRICS[item.metric];
      if(metricName && metricName.label) {
        return metricName.label
      }
      return "";
    },

    getMetricReverse(item) {
      const metricName = Vue.WyConstants.ALERT_METRICS[item.metric];
      if(metricName && metricName.reverse) {
        return metricName.reverse
      }
      return false;
    },

    getProcessName(item) {
      if(item.action == 'etl') {
        return "ETL Update";
      }
      else if(item.action == 'handedOffRequest') {
        return "Handed Off Intents Update";
      }
      else {
        return this.firstCap(item.action);
      }
    },

    getTime(date) {
      if(date && date != "") {
        return moment.utc(date).format("h:mm:ssa");
      }
      else {
        return "";
      }
    },

    getDate(date) {
      let formatDate = moment.utc(date).format('DD MMMM YYYY');
      return formatDate;
    },

    getDisplayDateWithoutYear(date) {
      let formatDate =  moment.utc(date).format('MMMM Do');
      return formatDate;
    },

    getUser(email) {
      return  email.match(/^([^@]*)@/)[1];
    },

    showProcessList({visible, tab}) {
      this.activeName = tab || (this.processNewCounter > 0 ? "activities" : "alerts");
      this.visible = visible;
      this.activitiesRead = false;
      this.alertsRead = false;
      this.handleClick();
    },

    logAmplitudeEvent() {
      if (!this.visible) return;

      const { alertList = [], amplitudeMetricMapper, alertNewCounter } = this;
      const newAlertList = alertList.filter(alert => alert.isNew)

      const latestAlertDate = newAlertList.length ? new Date(newAlertList[0].actionDate) : null;
      const firstUncheckedAlertDate = newAlertList.length ? new Date(newAlertList[newAlertList.length - 1].actionDate) : null;
      
      const additionalProps = {
        had_new_alerts_notification: Boolean(alertNewCounter > 0),
        time_since_latest_alert: latestAlertDate ? WyUtils.getTimeDiff(latestAlertDate) : 0,
        time_since_first_unchecked_alert: firstUncheckedAlertDate ? WyUtils.getTimeDiff(firstUncheckedAlertDate) : 0,
        num_accumulated_alerts: newAlertList.length,
        // more attributes added below
      };

      // Count the unchecked alerts for each category and add to additionalProps
      for (const metric in amplitudeMetricMapper) {
        const categoryAlertCount = newAlertList.filter(alert => alert.metric === metric).length;
        additionalProps[amplitudeMetricMapper[metric]] = categoryAlertCount;
      }

      this.$router.logAmplitudeEvent({ eventName: 'notification_center_opened', eventInfo: additionalProps });
    },

    formatNumber(value) {
      return WyUtils.formatterNumber(value);
    },

    formatDate(date) {
      return moment(date).format("YYYY-MM-DD HH:mm:ss");
    },

    firstCap(val) {
      return WyUtils.capitalizeFirstLetter(val).replace("Etl", "ETL");
    },

    unReadAlertDate(item) {
      const alertLastActionDate = this.getBrowserLocalStorageByUser("alertLastActionDate")
      const lastDate = moment(alertLastActionDate);
      return moment(item.actionDate).isAfter(lastDate);
    },
    unReadProcessDate(item) {
      const processLastActionDate = this.getBrowserLocalStorageByUser("processLastActionDate")
      const lastDate = moment(processLastActionDate);
      return moment(item.actionDate).isAfter(lastDate);
    },

    handleClosed() {
      this.tooltipContent = null;
      this.$refs.tooltip.doClose();
      
      if(this.activitiesRead) {
        this.resetProcessNewItems();
        this.activitiesRead = false;
      }
      if(this.alertsRead) {
        this.resetAlertNewItems();
        this.alertsRead = false;
      }

      this.logAmplitudeEvent();
    },

    showTooltip(e, processItem) {
      e.preventDefault();
      e.stopPropagation();

      if(this.$refs.tooltip) {
        if(!this.$refs.tooltip.showPopper) {
          this.$refs.tooltip.doClose();
          this.tooltipProcessId = null;
          this.tooltipProcessDetail = null;

          setTimeout(() => {          
            this.$refs.tooltip.referenceElm = e.target;
            this.tooltipProcessId = processItem.processId;
            this.tooltipProcessDetail = processItem.detail;
            this.$refs.tooltip.doShow();
          }, 300)
        }
        else {
          this.tooltipContent = null;
          this.$refs.tooltip.doClose();
        }
      }
    },

    handleDrawerClick(e) {
      e.preventDefault();
      e.stopPropagation();
      this.tooltipContent = null;
      this.$refs.tooltip.doClose();
    },
    
    handleTooltipOver(e, alert) {
      if(this.$refs.alertTooltip){
        if(alert.ruleOperator == "+") {
          this.alertTooltipContent = alert.ruleLastDateNumber == 1 ? `relative increase (by %) over previous ${alert.ruleLastDateNumber} day` : `relative increase (by %) over previous ${alert.ruleLastDateNumber} days`;
        }
        else if(alert.ruleOperator == "-") {
          this.alertTooltipContent = alert.ruleLastDateNumber == 1 ? `relative decrease (by %) over previous ${alert.ruleLastDateNumber} day` : `relative decrease (by %) over previous ${alert.ruleLastDateNumber} days`;
        }
        else if((alert.ruleOperator == ">=" || alert.ruleOperator == "<=") && alert.display.changeFlag==0) {
          this.alertTooltipContent = `equal to absolute threshold value`;
        }
        else if(alert.ruleOperator == ">=") {
          this.alertTooltipContent = `greater than absolute threshold value`;
        }
        else if(alert.ruleOperator == "<=") {
          this.alertTooltipContent = `less than absolute threshold value`;
        }
        else if(alert.ruleOperator == "+#") {
          this.alertTooltipContent = alert.ruleLastDateNumber == 1 ? `absolute increase (by #) over previous ${alert.ruleLastDateNumber} day` : `absolute increase (by #) over previous ${alert.ruleLastDateNumber} days`;
        }
        else if(alert.ruleOperator == "-#") {
          this.alertTooltipContent = alert.ruleLastDateNumber == 1 ? `absolute decrease (by #) over previous ${alert.ruleLastDateNumber} day` : `absolute decrease (by #) over previous ${alert.ruleLastDateNumber} days`;
        }

        this.$refs.alertTooltip.hide();
        this.$refs.alertTooltip.handleClosePopper();
        this.$refs.alertTooltip.referenceElm = e.target;
        this.$refs.alertTooltip.show();
      }
    },

    handleTooltipOut() {
      if(this.$refs.alertTooltip){
        this.$refs.alertTooltip.hide();
        this.$refs.alertTooltip.handleClosePopper();
      }
    }
  },

  async created() {
    this.$root.$on("fire-page-events:processListDrawer", async (data) => {
      this.notificationViewTime = new Date();
      if(data.refreshList) {
        await this.fetchProcessList();
      }
      this.showProcessList(data);
      if(!this.isAlertListAllowed) {
        this.activeName = 'activities';
        this.handleClick()
      }
      if(data.duration) {
        setTimeout(() => { this.showProcessList({visible: false}); }, data.duration)
      }

      this.logAmplitudeEvent();
    });
    if(!this.hideTitle) {
      this.title = 'Notifications'
    }
    else {
      this.title = '';
    }
  }
});
</script>

<style lang="scss">
.wy-process-list-drawer {
  .el-drawer__header {
    margin-bottom: 8px;
    outline: none;
    padding: 20px 20px 0 40px;
    > span{
      outline: none;
      font-size: 20px;
      font-weight: 400;
      color: var(--site-font-secondary-color);
    }
  }

  .el-drawer {
    top: 74px;  
    height: 100%;
  }

  .wy-process-panel {
    overflow-y: auto;
    // height: calc(100vh - 127px);
    max-height: calc(100vh - 176px);
    //border: 1px solid red;    
    // padding: 4px 14px 14px 20px;

    .wy-process-panel-label {
      display: inline-block;
      width: 100px;
      text-align: left;      
    }

    .wy-process-panel-info {
      cursor: pointer;
    }

    .wy-process-panel-item.active {
      background-color: #E8F1FF;
    }
    .wy-process-panel-item {
      font-size: 14px;
      background-color: #FFF;
      padding: 25px 15px 20px 15px;
      border-bottom: 1px solid #CAD2DA;
      position: relative;
      
      .status-icon {
        background-repeat: no-repeat;
        width:15px;
        height: 15px;
        position: absolute;
        top: 61px;
        left: 40px;
      }
      .completed-green {
        background-image: url('../assets/icons/icon_alert_completed.svg');
      }
      .failed-red {
        background-image: url('../assets/icons/icon_alert_failed.svg');
      }
      .pending-yellow {
        background-image: url('../assets/icons/icon_alert_pending.svg');
      }
      .div-item:first-child {
        padding-top: 0px;
      }
      .div-item {
        padding: 5px 0;
        padding-left: 25px;
        .label {
          width: 80px;
          display: inline-block;
          color: var(--site-font-tertiary-color);
          font-size: 16px;
          font-weight: 400;
          // position: relative;
          // top: 2px
        }
        .label.ellipsis.has-tooltip {
          cursor: pointer;
        }
        .content {
          display: inline-block;
          color: var(--site-font-secondary-color);
          font-weight: 400;
          font-size: 14px;
        }
        .label.ellipsis {
          word-break: break-word;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        span.sub-content {
          color: var(--site-font-tertiary-color);
          font-size: 11.2px;
          font-weight: 300;
        }

        .change-value {
          padding-left: 10px;
          font-size: 12px;
          font-weight: 700;
          display: inline-block;
          width: 80px;
          text-align: left;
        }
        .change-value.red {
          color: #C11149;
        }
        .change-value.green {
          color: #186731;
        }
        .value {
          padding-left: 0px;
          display: inline-block;
          width: 65px;
          text-align: right;
        }
      }
      .div-header-title {
        color: #6A767C;
        position: relative;
        font-size: 12px;
        font-weight: 500;
        .blue-dot {
          height: 8px;
          width: 8px;
          background-color: #1A55AD;
          border-radius: 50%;
          display: flex;
          position: absolute;
          top: 4px;
          left: 2px;
        }
        .product {
          font-size: 12px;
          font-weight: 500;
        }
        .dot {
          position: relative;
          display: inline-flex;
          align-items: center;
          top: 0px;
          margin: 0px 4px 0px 5px;
          width: 3px;
          height: 3px;
          background-color: #606060;
          border-radius: 1.5px;
        }
        .date {
          font-size: 12px;
          font-weight: 400;
        }
      }
      
      .div-title {
        color: var(--site-font-secondary-color);
        font-size: 20px;
        font-weight: 700;
        // padding: 20px 0px 35px 0px;
        padding: 12px 0px 0px 0px;
        padding-left: 50px;
        margin-bottom: 6px;
        // position: relative;
        p.ellipsis.has-tooltip {
          cursor: pointer;
        }
        p.ellipsis {
          word-break: break-word;
          // white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;

          -webkit-line-clamp: 5;
          -webkit-box-orient: vertical;
          display: -webkit-box;
          margin-top: 0px;
          margin-bottom: 0px;
        }
        .addition-info {
          width: 320px;
          color: var(--site-font-tertiary-color);
          font-size: 12px;
          font-weight: 300;
          // position: absolute;
          // top: 45px;
          // left: 27px;
          padding-left: 0px;
          margin-top: 6px;

          p.ellipsis {
            font-weight: 400;
          }
        }
      }

      > div {
        padding: 4px;

        > label {
          font-weight: bold;
        }

        > .status.finished {
          color: green
        }
        > .status.failed {
          color: red
        }
        > .status.running {
          color: #656500
        }
        > .status.pending {
          color: #374bad
        }
      }

      .detail {
        color: #444;
        padding-top: 8px;
      }

      .reload {        
        > label {
          text-decoration: underline;
          cursor: pointer;
          color: green
        }
      }
    }
    &::-webkit-scrollbar {
      background-color: white !important;
      width: 6px !important;
    }
    &::-webkit-scrollbar-thumb{
      background: #CAD2DA !important;
      border-radius: 50px !important; 
    } 
    .no-contact-reason {
      padding-left: 30px;
      text-align: center;
    }
  }

  .el-tabs.notification-only {
    #tab-activities {
      margin-left: 20px;
      padding-left: 20px;
      padding-right: 20px;
      margin-right: 20px;
    }
    #tab-activities.is-active {
      border-bottom: 2px solid #1A55AD;
    }
  }
  .el-tabs {
    .el-tabs__header {
      margin: 0px;
    }
    .el-tabs__content {
      
      .div-content {
        padding: 20px 20px;
        background-color: #F4F6F8;
        color: #4B5158;
        font-size: 12px;
        font-weight: 400;
      }
      .wy-alert-panel {
        overflow-x: hidden;
        overflow-y: auto;
        // height: calc(100vh - 249px);
        max-height: calc(100vh - 176px);
        .wy-alert-panel-item.active {
          background-color: #E8F1FF;
        }
        .wy-alert-panel-item {
          background-color: #FFF;
          padding: 20px 15px;
          border-bottom: 1px solid #CAD2DA;
          position: relative;
          .blue-dot {
            height: 8px;
            width: 8px;
            background-color: #1A55AD;
            border-radius: 50%;
            display: flex;
            position: absolute;
            top: 32px;
            left: 20px;
          }
          .div-item:first-child {
            // padding-top: 0px;
          }
          .div-item {
            padding: 5px 0;
            padding-left: 25px;
            .label {
              width: 210px;
              display: inline-block;
              color: var(--site-font-tertiary-color);
              font-size: 16px;
              font-weight: 400;
              position: relative;
              top: 2px
            }
            .label.ellipsis {
              word-break: break-word;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .label.ellipsis.has-tooltip {
              cursor: pointer;
            }
            span.sub-content {
              color: var(--site-font-tertiary-color);
              font-size: 12px;
              font-weight: 400;
            }

            .change-value {
              cursor: default;
              padding-left: 10px;
              font-size: 14px;
              font-weight: 700;
              display: inline-block;
              width: 65px;
              text-align: left;
            }
            .el-icon-bottom, .el-icon-top {
              font-weight: 600;
            }
            .change-value.red {
              color: #C11149;
            }
            .change-value.green {
              color: #186731;
            }
            .change-value.gray {
              color: var(--site-font-tertiary-color);
            }
            .value {
              padding-left: 0px;
              display: inline-block;
              width: 65px;
              text-align: right;
              font-size: 18px;
            }
            .change-flag-na {
              color: var(--site-font-tertiary-color);
              font-weight: 400
            }
          }
          .div-header-title {
            color: #6A767C;
            // line-height: 0px;
            .product {
              font-size: 12px;
              font-weight: 500;
            }
            // .dot {
            //   font-size: 8px;
            //   position: relative;
            //   top: -1px;
            //   padding: 0px 3px;
            //   color: #606060;
            // }
            .dot {
              position: relative;
              display: inline-flex;
              align-items: center;
              top: 0px;
              margin: 0px 4px 0px 5px;
              width: 3px;
              height: 3px;
              background-color: #606060;
              border-radius: 1.5px;
            }
            .date {
              font-size: 12px;
              font-weight: 400;
            }
          }
          .div-title {
            color: var(--site-font-secondary-color);
            font-size: 20px;
            font-weight: 700;
            padding: 21px 0px;
            padding-left: 25px;
          }
        }
        &::-webkit-scrollbar {
          background-color: white !important;
          width: 6px !important;
        }
        &::-webkit-scrollbar-thumb{
          background: #CAD2DA !important;
          border-radius: 50px !important; 
        } 
        .no-contact-reason {
          padding-left: 30px;
          text-align: center;
        }
        .no-data-content {
          font-size: 16px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
          color: var(--site-font-secondary-color);
          text-align: left;
          margin-left: 40px;
          margin-top: 35px;
          width: 355px;
        }
        .config-link {
          font-size: 16px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
          text-decoration-line: underline;
          color: #1A55AD;
          text-align: left;
          margin-left: 40px;
          margin-top: 10px;
          cursor: pointer;
        }
      }
      
    }
    .el-tabs__active-bar.is-top {
      // margin-left: 22px;
      margin-left: -20px;
      width: 92px !important;
      display: none;
    }
    #tab-alerts {
      margin-left: 20px;
      padding-left: 20px;
      padding-right: 20px;
      margin-right: 20px;
    }
    #tab-alerts.is-active {
      border-bottom: 2px solid #1A55AD;
    }
    #tab-activities {
      padding-left: 10px;
      padding-right: 10px;
    }
    #tab-activities.is-active {
      border-bottom: 2px solid #1A55AD;
    }
    .el-tabs__item {
      font-size: 16px;
      font-weight: 400;
      color: var(--site-font-primary-color);
      height: 45px;
    }
    .el-tabs__item.is-active {
      color: var(--notification-tab-title-active-color);
    }
  }
}

.wy-popover-tooltip-processlist {
  background-color: #444;
  color: #fff;
  border: none;
  font-size: 14px !important;

  div.content {    
    label {
      font-weight: bold;
    }

    .vjs-tree {
      font-family: inherit;
      font-size: 12px;
    }

    .vjs-tree-node.is-highlight, .vjs-tree-node:hover {
      background-color: unset;
    }
  }
}

#app .site-tooltips.black-background.notification {
  max-height: 300px;
  max-width: 300px;
  // overflow-y: auto; // Causing issues 893902 #3
  border-radius: 5px;

  &::-webkit-scrollbar {
    width: 20px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 160px;
    border: 2px solid transparent;
    border-top: 4px solid transparent;
    background-clip: content-box;
    background-color: #CAD2DA;
  }
  &::-webkit-scrollbar-track {
    background:#fff;
    border-top: 1px solid #CAD2DA;
  }
  &::-webkit-scrollbar:vertical {
    width: 14px;
  }
  &::-webkit-scrollbar-thumb:vertical {
    border-radius: 160px;
    border: 2px solid transparent;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    background-clip: content-box;
    background-color: #CAD2DA;
  }
  &::-webkit-scrollbar-track:vertical {
    //border-left: 1px solid #CAD2DA;
    border-top: none !important;
    background-color: #303133;
  }
}
</style>